import { useEffect, useState } from "react";
import { getPublicCountyStats } from "../api";

/** One live per-category lead count from /public/county-stats. `category`
 *  matches notice_leads.record_type (foreclosure | probate | tax_foreclosure |
 *  hoa_foreclosure). */
export interface LiveLeadCategory {
  category: string;
  total: number;
  addressed: number;
}

/** One live full-parcel-depth county. `county_id` maps to /public/<county_id>. */
export interface LiveDeepCounty {
  county_id: string;
  county_name: string;
}

interface CountyStatsResponse {
  lead_categories?: LiveLeadCategory[];
  deep_counties?: LiveDeepCounty[];
}

/**
 * Live landing-coverage stats from `/public/county-stats`.
 *
 * Returns the per-category lead inventory keyed by category, plus the
 * full-parcel-depth county list. Both stay `null` until the fetch resolves and
 * on any failure (or when the endpoint omits the key — same graceful-
 * degradation contract as `useAddressedLeadsCount`, which reads `leads_total`
 * from the same response), so the landing renders its hardcoded snapshot as a
 * fallback rather than a blank or a fake zero. This replaces the ~4-week-stale
 * hardcoded category numbers and county count so a marketing "truth page"
 * can't silently drift again.
 */
export function usePublicCountyStats() {
  const [leadCategories, setLeadCategories] = useState<Record<string, LiveLeadCategory> | null>(null);
  const [deepCounties, setDeepCounties] = useState<LiveDeepCounty[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPublicCountyStats<CountyStatsResponse>()
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data?.lead_categories) && data.lead_categories.length > 0) {
          const byKey: Record<string, LiveLeadCategory> = {};
          for (const c of data.lead_categories) byKey[c.category] = c;
          setLeadCategories(byKey);
        }
        if (Array.isArray(data?.deep_counties) && data.deep_counties.length > 0) {
          setDeepCounties(data.deep_counties);
        }
      })
      .catch(() => {
        // Leave both null; the landing uses its hardcoded fallbacks.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { leadCategories, deepCounties };
}
